import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { serviceDetails, serviceDetailUI, serviceImages, serviceSlugs, serviceGalleries } from "@/i18n/serviceDetails";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ServiceGallery from "@/components/site/ServiceGallery";
import ServiceMarketingSection from "@/components/site/ServiceMarketingSection";

const CARLISLE_LOGO =
  "https://customer-assets.emergentagent.com/job_castel-new-look/artifacts/mphru06k_carlisle-logo-4c.svg";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const ui = serviceDetailUI[lang];
  const data = serviceDetails[lang]?.[slug];
  const image = serviceImages[slug];
  const isCarlisle = slug === "carlisle-membranes";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="container-page py-40 flex-1">
          <h1 className="font-display text-5xl">404</h1>
          <button onClick={() => navigate("/")} className="mt-6 underline">{ui.back}</button>
        </main>
        <Footer />
      </div>
    );
  }

  const related = serviceSlugs.filter((s) => s !== slug);

  return (
    <div className="App bg-[var(--bg)] text-[var(--ink)]" data-testid={`service-page-${slug}`}>
      <Navbar />

      {/* Hero band */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 border-b border-[var(--border)]">
        <div className="container-page">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.18em] text-neutral-500 hover:text-black transition-colors mb-12"
          >
            <span aria-hidden>←</span> {ui.back}
          </Link>

          <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-6" data-testid="service-eyebrow">
            <span className="w-8 h-px bg-neutral-400" />
            {data.eyebrow}
          </div>

          <h1
            data-testid="service-title"
            className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.02em] leading-[1.02] max-w-5xl"
          >
            {data.title}
          </h1>

          {isCarlisle && (
            <div className="mt-8 flex items-center gap-4" data-testid="service-carlisle-badge">
              <img src={CARLISLE_LOGO} alt="CARLISLE®" className="h-7 w-auto" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                · Authorized Installer
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Image */}
      <section className="py-12 md:py-16">
        <div className="container-page">
          <div className="relative aspect-[16/9] overflow-hidden bg-neutral-100" data-testid="service-hero-image">
            <img src={image} alt={data.title} className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Body content */}
      <section className="pb-24 md:pb-36">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <p data-testid="service-intro" className="font-display text-2xl md:text-3xl leading-[1.25] tracking-tight text-black">
                {data.intro}
              </p>

              {data.body && (
                <p data-testid="service-body" className="mt-10 text-lg text-neutral-700 leading-relaxed">
                  {data.body}
                </p>
              )}

              {/* Bullets */}
              <div className="mt-12" data-testid="service-bullets">
                {data.listTitle && (
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-6">
                    {data.listTitle}
                  </div>
                )}
                <ul className="space-y-px bg-[var(--border)]">
                  {data.bullets.map((b, i) => (
                    <li
                      key={i}
                      data-testid={`service-bullet-${i}`}
                      className="bg-[var(--bg)] flex items-start gap-5 px-1 py-5 group"
                    >
                      <span className="font-mono text-xs text-neutral-400 mt-2 w-8 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-lg md:text-xl text-black leading-snug group-hover:text-[var(--accent)] transition-colors">
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {data.outro && (
                <p data-testid="service-outro" className="mt-12 text-lg text-neutral-700 leading-relaxed max-w-2xl">
                  {data.outro}
                </p>
              )}
            </div>

            {/* Side: CTA card + related */}
            <aside className="lg:col-span-5 lg:pl-8 lg:border-l border-[var(--border)]">
              <div className="bg-[var(--bg-dark)] text-white p-8 md:p-10 sticky top-28" data-testid="service-cta-card">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-4">
                  {t.contact.eyebrow}
                </div>
                <div className="font-display text-3xl md:text-4xl leading-tight">
                  {t.contact.title}
                </div>
                <p className="mt-4 text-white/70 leading-relaxed">{t.contact.lead}</p>
                <Link
                  to="/#contact"
                  data-testid="service-cta-btn"
                  className="group mt-8 inline-flex items-center justify-between gap-3 bg-white text-black px-6 py-4 w-full hover:bg-[var(--accent)] hover:text-white transition-colors"
                >
                  <span>{ui.cta}</span>
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </Link>

                <div className="mt-8 pt-8 border-t border-white/15 space-y-4">
                  <a href="tel:+37368257835" className="block hover:text-[var(--accent)] transition-colors">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{t.contact.info.phone}</div>
                    <div className="font-display text-xl mt-1">+373 68 257 835</div>
                  </a>
                  <a href="mailto:castelconstructsrl@gmail.com" className="block hover:text-[var(--accent)] transition-colors break-all">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{t.contact.info.email}</div>
                    <div className="font-display text-base mt-1">castelconstructsrl@gmail.com</div>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Project gallery (only for services that have one) */}
      {serviceGalleries[slug] && (
        <ServiceGallery items={serviceGalleries[slug]} lang={lang} title={ui.galleryTitle} />
      )}

      {/* Marketing section (currently for CARLISLE® only) */}
      {data.marketingSection && (
        <ServiceMarketingSection
          section={data.marketingSection}
          ctaLabel={ui.cta}
          contactTitle={t.contact.title}
        />
      )}

      {/* Related services */}
      <section className="border-t border-[var(--border)] py-20 md:py-28 bg-[var(--bg-secondary)]" data-testid="related-services">
        <div className="container-page">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-8">
            {ui.relatedTitle}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
            {related.map((s, i) => {
              const r = serviceDetails[lang][s];
              return (
                <Link
                  key={s}
                  to={`/services/${s}`}
                  data-testid={`related-${s}`}
                  className="bg-[var(--bg-secondary)] hover:bg-white transition-colors p-8 md:p-10 group flex flex-col gap-6"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-neutral-200">
                    <img src={serviceImages[s]} alt={r.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{r.eyebrow}</div>
                    <div className="mt-3 font-display text-2xl md:text-3xl leading-tight group-hover:text-[var(--accent)] transition-colors">
                      {r.title}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
